(() => {
  class AspadzCustomerStories extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.retryTimer = null;
      this.desktopQuery = window.matchMedia('(min-width: 750px)');
      this.dialog = null;
      this.dialogImage = null;
      this.activeTrigger = null;
      this.restoreFocusOnClose = false;
      this.abortController = null;
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleDialogCancel = this.handleDialogCancel.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
    }

    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      this.dialog = this.querySelector('[data-aspadz-customer-stories-dialog]');
      this.dialogImage = this.dialog?.querySelector('[data-aspadz-customer-stories-dialog-image]') || null;

      this.addQueryListener(this.desktopQuery, this.handleViewportChange);
      this.addEventListener('click', this.handleClick, { signal });
      this.dialog?.addEventListener('cancel', this.handleDialogCancel, { signal });
      this.dialog?.addEventListener('click', this.handleDialogClick, { signal });
      this.dialog?.addEventListener('close', this.handleDialogClose, { signal });
      this.init();
    }

    disconnectedCallback() {
      window.clearTimeout(this.retryTimer);
      this.closeDialog(false);
      this.abortController?.abort();
      this.abortController = null;
      this.removeQueryListener(this.desktopQuery, this.handleViewportChange);
      this.destroySwiper();
      this.dialog = null;
      this.dialogImage = null;
      this.activeTrigger = null;
    }

    addQueryListener(query, callback) {
      if (query.addEventListener) {
        query.addEventListener('change', callback);
      } else {
        query.addListener(callback);
      }
    }

    removeQueryListener(query, callback) {
      if (query.removeEventListener) {
        query.removeEventListener('change', callback);
      } else {
        query.removeListener(callback);
      }
    }

    init() {
      if (this.swiper) return;

      if (typeof window.Swiper === 'undefined') {
        this.retryTimer = window.setTimeout(() => this.init(), 120);
        return;
      }

      const slider = this.querySelector('[data-aspadz-customer-stories-slider]');
      const slides = this.querySelectorAll('.aspadz-customer-stories__slide');
      if (!slider || slides.length < 2) return;

      const desktopContentOffset = Math.max(24, (document.documentElement.clientWidth - 1540) / 2);

      this.swiper = new window.Swiper(slider, {
        allowTouchMove: true,
        autoplay: false,
        centeredSlides: false,
        initialSlide: 0,
        loop: false,
        navigation: {
          nextEl: this.querySelector('[data-aspadz-customer-stories-next]'),
          prevEl: this.querySelector('[data-aspadz-customer-stories-prev]'),
        },
        pagination: {
          clickable: true,
          el: this.querySelector('[data-aspadz-customer-stories-pagination]'),
          renderBullet(index, className) {
            return `
              <button class="${className}" type="button" aria-label="Go to slide ${index + 1}">
                <span class="aspadz-customer-stories__pagination-track">
                </span>
              </button>
            `;
          },
        },
        slidesPerView: 'auto',
        spaceBetween: 15,
        speed: 650,
        watchOverflow: true,
        breakpoints: {
          750: {
            slidesOffsetBefore: desktopContentOffset,
            spaceBetween: 20,
          },
        },
      });
    }

    destroySwiper() {
      if (!this.swiper) return;

      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    handleViewportChange() {
      if (!this.desktopQuery.matches) this.closeDialog(false);
      this.destroySwiper();
      this.init();
    }

    handleClick(event) {
      const trigger = event.target.closest('[data-aspadz-customer-stories-popup-trigger]');
      if (trigger && this.contains(trigger)) {
        if (this.desktopQuery.matches) this.openDialog(trigger);
        return;
      }

      const closeButton = event.target.closest('[data-aspadz-customer-stories-dialog-close]');
      if (closeButton && this.contains(closeButton)) this.closeDialog();
    }

    handleDialogCancel(event) {
      event.preventDefault();
      this.closeDialog();
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.closeDialog();
    }

    handleDialogClose() {
      const focusTarget = this.restoreFocusOnClose ? this.activeTrigger : null;

      window.AspadzScrollLock?.unlock(this.dialog);
      this.activeTrigger = null;
      this.restoreFocusOnClose = false;

      if (focusTarget?.isConnected) {
        window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      }
    }

    openDialog(trigger) {
      if (!this.dialog || !this.dialogImage || !trigger.dataset.popupSrc) return;

      this.activeTrigger = trigger;
      this.restoreFocusOnClose = true;
      this.dialogImage.width = Number(trigger.dataset.popupWidth) || 1;
      this.dialogImage.height = Number(trigger.dataset.popupHeight) || 1;
      this.dialogImage.src = trigger.dataset.popupSrc;
      this.dialogImage.srcset = trigger.dataset.popupSrcset || '';
      this.dialogImage.sizes = 'min(1200px, calc(100vw - 80px))';
      this.dialogImage.alt = trigger.dataset.popupAlt || '';
      this.dialog.setAttribute('aria-label', trigger.getAttribute('aria-label') || '');

      window.AspadzScrollLock?.lock(this.dialog);
      if (!this.dialog.open) this.dialog.showModal();

      window.requestAnimationFrame(() => {
        this.dialog.querySelector('[data-aspadz-customer-stories-dialog-close]')?.focus({ preventScroll: true });
      });
    }

    closeDialog(restoreFocus = true) {
      if (!this.dialog?.open) return;
      this.restoreFocusOnClose = restoreFocus;
      this.dialog.close();
    }

  }

  if (!window.customElements.get('aspadz-customer-stories')) {
    window.customElements.define('aspadz-customer-stories', AspadzCustomerStories);
  }
})();
